import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Users as UsersIcon, Search } from 'lucide-react';
import { useEmployees } from '../hooks';
import { EmployeeCard } from './EmployeeCard';
import { EmptyEmployeesState } from './EmptyEmployeesState';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import type { Employee, EmployeesQueryParams } from '../types';

interface EmployeesListProps {
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

export function EmployeesList({
  onRefresh,
  isRefreshing = false,
}: EmployeesListProps) {
  const { t } = useTranslation();
  const { folderUuid } = useParams();

  const [queryParams, setQueryParams] = useState<EmployeesQueryParams>({
    page: 1,
    limit: 12,
    status: 'all',
    contractStatus: 'all',
  });

  const [searchTerm, setSearchTerm] = useState('');

  const {
    data: employeesData,
    isLoading,
    error,
  } = useEmployees(folderUuid, {
    ...queryParams,
    search: searchTerm,
  });

  const employees = useMemo(
    () => employeesData?.data ?? [],
    [employeesData?.data]
  );

  const filteredEmployees = useMemo(() => {
    return employees.filter((employee: Employee) => {
      const matchesStatus =
        queryParams.status === 'all'
          ? true
          : queryParams.status === 'active'
            ? (employee.isActive ?? false)
            : !(employee.isActive ?? false);

      const matchesContract =
        queryParams.contractStatus === 'all'
          ? true
          : queryParams.contractStatus === 'active'
            ? (employee.contratActif ?? false)
            : !(employee.contratActif ?? false);

      return matchesStatus && matchesContract;
    });
  }, [employees, queryParams.status, queryParams.contractStatus]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setQueryParams(prev => ({ ...prev, page: 1 }));
  };

  const handleStatusFilter = (status: 'all' | 'active' | 'inactive') => {
    setQueryParams(prev => ({ ...prev, status, page: 1 }));
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <LoadingSkeleton className="h-10 w-64" />
          <LoadingSkeleton className="h-10 w-48" />
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <LoadingSkeleton key={i} className="h-64 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-error-50 rounded-lg border border-red-200 p-6 text-center">
        <h3 className="text-error-900 mb-2 text-lg font-semibold">
          {t('employees.error.title', 'Erreur')}
        </h3>
        <p className="text-error-700 text-sm">
          {t(
            'employees.error.description',
            'Impossible de charger la liste des employés'
          )}
        </p>
      </div>
    );
  }

  const hasEmployees = filteredEmployees.length > 0;
  const totalEmployees = employeesData?.total ?? 0;

  return (
    <div className="space-y-6">
      {/* Header avec recherche et filtres */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-primary-100 flex h-10 w-10 items-center justify-center rounded-lg">
            <UsersIcon className="text-primary-600 h-5 w-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">
              {t('employees.title', 'Employés')}
            </h2>
            <p className="text-sm text-neutral-500">
              {totalEmployees} {t('employees.employeesCount', 'employé(s)')}
            </p>
          </div>
        </div>

        {/* Recherche */}
        {totalEmployees > 0 && (
          <div className="relative flex-1 sm:max-w-sm">
            <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <input
              type="text"
              placeholder={t(
                'employees.search.placeholder',
                'Rechercher un employé...'
              )}
              value={searchTerm}
              onChange={e => handleSearch(e.target.value)}
              className="focus:border-primary-500 focus:ring-primary-500 w-full rounded-lg border border-neutral-300 py-2 pr-4 pl-10 text-sm transition-colors focus:ring-2 focus:outline-none"
            />
          </div>
        )}
      </div>

      {/* Filtres */}
      {totalEmployees > 0 && (
        <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-neutral-700">
          {t('employees.filters.title', 'Filtrer par')}:
        </span>

        <button
          onClick={() => handleStatusFilter('all')}
          className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
            queryParams.status === 'all'
              ? 'bg-primary-600 text-white'
              : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
          }`}
        >
          {t('employees.filters.all', 'Tous')}
        </button>

        <button
          onClick={() => handleStatusFilter('active')}
          className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
            queryParams.status === 'active'
              ? 'bg-success-600 text-white'
              : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
          }`}
        >
          {t('employees.filters.active', 'Actifs')}
        </button>

        <button
          onClick={() => handleStatusFilter('inactive')}
          className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
            queryParams.status === 'inactive'
              ? 'bg-error-600 text-white'
              : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
          }`}
        >
          {t('employees.filters.inactive', 'Inactifs')}
        </button>
        </div>
      )}

      {/* Liste des employés */}
      {totalEmployees === 0 ? (
        <EmptyEmployeesState
          onRefresh={onRefresh}
          isRefreshing={isRefreshing}
        />
      ) : hasEmployees ? (
        <>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filteredEmployees.map(employee => (
              <EmployeeCard key={employee.uuid} employee={employee} />
            ))}
          </div>

          {/* Pagination */}
          {employeesData && employeesData.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() =>
                  setQueryParams(prev => ({
                    ...prev,
                    page: Math.max(1, prev.page! - 1),
                  }))
                }
                disabled={!employeesData.hasPreviousPage}
                className="rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t('common.previous', 'Précédent')}
              </button>

              <span className="text-sm text-neutral-600">
                {t('common.page', 'Page')} {employeesData.page} /{' '}
                {employeesData.totalPages}
              </span>

              <button
                onClick={() =>
                  setQueryParams(prev => ({
                    ...prev,
                    page: (prev.page || 1) + 1,
                  }))
                }
                disabled={!employeesData.hasNextPage}
                className="rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t('common.next', 'Suivant')}
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-6 py-10 text-center">
          <p className="font-medium text-neutral-900">
            {t('employees.empty.noMatch', 'Aucun résultat')}
          </p>
          <p className="mt-1 text-sm text-neutral-600">
            {t(
              'employees.empty.noMatchHint',
              'Modifiez votre recherche ou vos filtres.'
            )}
          </p>
        </div>
      )}
    </div>
  );
}
